import type { HowToPlayPageData } from "@/components/HowToPlayPage";

export const lottoAmericaHowToPlayData: HowToPlayPageData = {
  meta: {
    title: "How to Play Lotto America: Rules & Prizes",
    description: "Learn how to play Lotto America. Understand the rules, all 9 prize tiers, the All Star Bonus multiplier, jackpot odds, and how to claim winnings.",
    canonical: "https://usalotteryclub.com/lotto-america/how-to-play",
  },
  gameName: "Lotto America",
  gameSlug: "lotto-america",
  heroDescription: "Lotto America is a multi-state lottery currently offered in 13 U.S. states. Drawings are held every Monday, Wednesday, and Saturday at 10:00 p.m. Central Time. The jackpot starts at $2 million and rolls over until won. An optional All Star Bonus multiplier can multiply non-jackpot prizes by up to 5x.",
  keyFacts: ["$1 per play", "Draws Mon/Wed/Sat", "Jackpot starts at $2M", "9 ways to win"],
  steps: [
    { title: "Pick your white ball numbers", description: "Choose five different numbers from 1 to 52." },
    { title: "Pick your Star Ball number", description: "Choose one Star Ball number from 1 to 10." },
    { title: "Add the All Star Bonus (optional)", description: "For an extra $1 per play, add the All Star Bonus multiplier — it multiplies any non-jackpot prize by 2x, 3x, 4x, or 5x." },
    { title: "Use Quick Pick if you prefer", description: "Mark Quick Pick and the terminal will randomly select your numbers for you." },
    { title: "Check the results", description: "Drawings are held Monday, Wednesday, and Saturday at 10:00 p.m. Central Time." },
  ],
  prizeTiersIntro: "There are 9 ways to win a Lotto America prize. The overall odds of winning any prize are 1 in 9.63.",
  prizeTiers: [
    { match: "5 white balls + Star Ball", prize: "Jackpot", odds: "1 in 25,989,600" },
    { match: "5 white balls", prize: "$20,000", odds: "1 in 2,887,733" },
    { match: "4 white balls + Star Ball", prize: "$1,000", odds: "1 in 110,594" },
    { match: "4 white balls", prize: "$100", odds: "1 in 12,288" },
    { match: "3 white balls + Star Ball", prize: "$20", odds: "1 in 2,404" },
    { match: "3 white balls", prize: "$5", odds: "1 in 267" },
    { match: "2 white balls + Star Ball", prize: "$5", odds: "1 in 160" },
    { match: "1 white ball + Star Ball", prize: "$2", odds: "1 in 29" },
    { match: "Star Ball only", prize: "$2", odds: "1 in 17" },
  ],
  multiplierTitle: "The All Star Bonus Multiplier",
  multiplierIntro: "Add the All Star Bonus for $1 extra per play. When you win any non-jackpot prize, it's multiplied by the drawn All Star Bonus number (2x, 3x, 4x, or 5x).",
  multiplierNote: "The All Star Bonus does not apply to the jackpot prize.",
  multiplierRows: [
    { match: "5 white balls", base: "$20,000", x2: "$40,000", x3: "$60,000", x4: "$80,000", x5: "$100,000", x10: "—" },
    { match: "4 + Star Ball", base: "$1,000", x2: "$2,000", x3: "$3,000", x4: "$4,000", x5: "$5,000", x10: "—" },
    { match: "4 white balls", base: "$100", x2: "$200", x3: "$300", x4: "$400", x5: "$500", x10: "—" },
    { match: "3 + Star Ball", base: "$20", x2: "$40", x3: "$60", x4: "$80", x5: "$100", x10: "—" },
    { match: "3 white balls", base: "$5", x2: "$10", x3: "$15", x4: "$20", x5: "$25", x10: "—" },
    { match: "2 + Star Ball", base: "$5", x2: "$10", x3: "$15", x4: "$20", x5: "$25", x10: "—" },
    { match: "1 + Star Ball", base: "$2", x2: "$4", x3: "$6", x4: "$8", x5: "$10", x10: "—" },
    { match: "Star Ball only", base: "$2", x2: "$4", x3: "$6", x4: "$8", x5: "$10", x10: "—" },
  ],
  jackpotTitle: "How the Lotto America Jackpot Works",
  jackpotDescription: "The Lotto America jackpot starts at $2 million and grows with every drawing where no one matches all six numbers.",
  jackpotOptions: [
    { label: "Annuity", description: "30 annual payments over 29 years, with each payment 5% larger than the last." },
    { label: "Cash (Lump Sum)", description: "A single one-time payment equal to the cash value of the jackpot prize pool." },
  ],
  jackpotFootnote: "If multiple tickets match all six winning numbers, the jackpot is divided equally among all winning plays.",
  drawSchedule: [
    { day: "Monday", time: "10:00 p.m. CT" },
    { day: "Wednesday", time: "10:00 p.m. CT" },
    { day: "Saturday", time: "10:00 p.m. CT" },
  ],
  drawScheduleNote: "Ticket sales close before each drawing — cutoff times vary by state.",
  claimRules: [
    "Small prizes (typically under $600): Claim at any authorized lottery retailer.",
    "Prizes $600 and above: Complete a claim form and visit a lottery office.",
    "Jackpot: Must be claimed in person at the lottery headquarters in the state where the winning ticket was purchased.",
    "Claim deadline: Usually 180 days to 1 year from the drawing date — varies by state.",
  ],
  claimNote: "Sign the back of your ticket immediately. All prizes must be claimed in the state where the ticket was purchased.",
  taxTitle: "Lotto America Taxes",
  taxSections: [
    { heading: "Federal withholding", text: "The IRS requires 24% to be withheld automatically from prizes over $5,000." },
    { heading: "State taxes", text: "Most participating states withhold state income tax. Rates vary by state." },
    { heading: "Annuity vs. lump sum taxes", text: "The lump sum is taxed in full in the year you receive it; annuity payments are taxed as ordinary income each year." },
  ],
  taxCta: "Use our Tax Calculator to estimate your take-home amount →",
  quickFacts: [
    { label: "Ticket price", value: "$1 per play (+ $1 for All Star Bonus)" },
    { label: "White ball range", value: "1–52" },
    { label: "Star Ball range", value: "1–10" },
    { label: "Drawing days", value: "Mon, Wed, Sat" },
    { label: "Drawing time", value: "10:00 p.m. CT" },
    { label: "Starting jackpot", value: "$2 million" },
    { label: "Overall odds", value: "1 in 9.63" },
    { label: "Jackpot odds", value: "1 in 25,989,600" },
    { label: "Ways to win", value: "9" },
  ],
  faqs: [
    { question: "How much does a Lotto America ticket cost?", answer: "A base Lotto America ticket costs $1 per play. Adding the All Star Bonus costs an extra $1 per play." },
    { question: "When are Lotto America drawings?", answer: "Drawings are held every Monday, Wednesday, and Saturday at 10:00 p.m. Central Time." },
    { question: "Which states play Lotto America?", answer: "Lotto America is offered in Delaware, Idaho, Iowa, Kansas, Maine, Minnesota, Montana, New Mexico, North Dakota, Oklahoma, South Dakota, Tennessee, and West Virginia." },
    { question: "What is the All Star Bonus?", answer: "An optional add-on that multiplies any non-jackpot prize by 2x, 3x, 4x, or 5x for an extra $1 per play." },
  ],
  disclaimer: "Results are unofficial. Always verify winning numbers with your official state lottery. USA Lottery Club is independent and not affiliated with Lotto America or any state lottery.",
};